import React, { useState, useMemo, useEffect } from 'react';
import { PALETTE } from './SiteLayout';
import { usePricing } from '../../i18n/pricing';

/**
 * RoiCalculator
 * -----------------
 * Three-input ROI calculator for the Pricing page.
 *
 * All monetary defaults + formatting come from usePricing() so the
 * calculator adapts to the visitor's market (EUR / PLN / HUF).
 *
 * Inputs:
 *   - team size (number of ops people drowning in spreadsheets)
 *   - hours per week each spends on spreadsheets / manual ops admin
 *   - average loaded annual salary per ops person (local currency)
 *
 * Output:
 *   - hours/year reclaimed
 *   - local-currency reclaimed / year
 *   - payback period in months on the local monthly licence
 */

const Field = ({ label, value, onChange, min, max, step = 1, suffix, testid, formatValue }) => (
  <label style={{ display: 'block', marginBottom: 22 }}>
    <div style={{
      fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase',
      fontWeight: 700, color: PALETTE.muted, marginBottom: 8,
    }}>{label}</div>
    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        data-testid={testid}
        style={{ flex: 1, accentColor: PALETTE.magenta }}
      />
      <div style={{
        minWidth: 130,
        textAlign: 'right',
        fontFamily: "'Playfair Display', Georgia, serif",
        fontWeight: 700,
        fontSize: 20,
        color: PALETTE.navy,
        letterSpacing: '-0.01em',
      }}>
        {formatValue ? formatValue(value) : value}
        {suffix && !formatValue && <span style={{ fontSize: 13, fontWeight: 500, color: PALETTE.muted, marginLeft: 4 }}>{suffix}</span>}
      </div>
    </div>
  </label>
);

const Stat = ({ label, value, accent }) => (
  <div style={{
    padding: '20px 22px',
    background: '#fff',
    border: `1px solid ${PALETTE.border}`,
    borderTop: `3px solid ${accent || PALETTE.navy}`,
  }}>
    <div style={{
      fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase',
      fontWeight: 700, color: PALETTE.muted, marginBottom: 8,
    }}>{label}</div>
    <div style={{
      fontFamily: "'Playfair Display', Georgia, serif",
      fontWeight: 700, fontSize: 30, color: PALETTE.navy,
      lineHeight: 1.05, letterSpacing: '-0.02em',
    }}>{value}</div>
  </div>
);

export default function RoiCalculator() {
  const p = usePricing();

  // Salary slider bounds scale with the local salary default so the
  // numbers stay realistic in each market (PLN and HUF are much larger
  // absolute numbers than EUR).
  const salaryStep = p.code === 'HUF' ? 100000 : p.code === 'PLN' ? 2000 : 1000;
  const salaryMin  = Math.round(p.annualStaffSalary * 0.6);
  const salaryMax  = Math.round(p.annualStaffSalary * 3);

  // Defaults are the brief's median-case scenario, in local currency.
  const [teamSize, setTeamSize] = useState(2);
  const [hoursPerWeek, setHoursPerWeek] = useState(6);
  const [annualSalary, setAnnualSalary] = useState(p.annualStaffSalary);

  // Reseed salary when the visitor changes market so the slider snaps to
  // the new market's default (€40k vs 96 000 zł vs 11 800 000 Ft).
  useEffect(() => {
    setAnnualSalary(p.annualStaffSalary);
  }, [p.code, p.annualStaffSalary]);

  // Local number formatter (drives all monetary values shown to the visitor).
  const numFmt = useMemo(
    () => new Intl.NumberFormat(p.locale || 'en-IE', { maximumFractionDigits: 0 }),
    [p.locale],
  );
  const money = useMemo(
    () => new Intl.NumberFormat(p.locale || 'en-IE', {
      style: 'currency', currency: p.code, maximumFractionDigits: 0, minimumFractionDigits: 0,
    }),
    [p.locale, p.code],
  );
  const NUMBER = (n) => numFmt.format(n);
  const MONEY = (n) => money.format(n);

  const result = useMemo(() => {
    const HOURLY_RATE = annualSalary / 1872; // 36hr/wk × 52 weeks, conservative
    const HOURS_RECLAIMED_PER_YEAR = teamSize * hoursPerWeek * 48; // 48 working weeks
    const VALUE_RECLAIMED = HOURS_RECLAIMED_PER_YEAR * HOURLY_RATE;
    return {
      hoursReclaimed: HOURS_RECLAIMED_PER_YEAR,
      valueReclaimed: VALUE_RECLAIMED,
      weeksReclaimed: HOURS_RECLAIMED_PER_YEAR / 36,
      isMeaningful: HOURS_RECLAIMED_PER_YEAR >= 150,
    };
  }, [teamSize, hoursPerWeek, annualSalary]);

  return (
    <section
      data-testid="roi-calculator"
      style={{
        padding: 'clamp(40px, 6vw, 64px) clamp(18px, 4vw, 32px)',
        background: PALETTE.cream,
        borderTop: `1px solid ${PALETTE.border}`,
        borderBottom: `1px solid ${PALETTE.border}`,
      }}
    >
      <div style={{ maxWidth: 1080, margin: '0 auto' }}>
        <div style={{
          fontSize: 11, letterSpacing: '0.24em', textTransform: 'uppercase',
          fontWeight: 700, color: PALETTE.magenta, marginBottom: 14,
        }}>
          Does the time add up?
        </div>
        <h2 style={{
          fontFamily: "'Playfair Display', Georgia, serif",
          fontSize: 'clamp(28px, 3.6vw, 40px)',
          fontWeight: 700,
          color: PALETTE.navy,
          margin: '0 0 16px',
          letterSpacing: '-0.02em',
          lineHeight: 1.1,
        }}>
          The reclaimed-hours calculator.
        </h2>
        <p style={{
          fontSize: 15.5, lineHeight: 1.65, color: PALETTE.slate,
          margin: '0 0 36px', maxWidth: 720,
        }}>
          <strong style={{ color: PALETTE.navy, fontWeight: 700 }}>Illustrative scenario.</strong> Plug in your own numbers. The calculator runs locally in your browser, nothing is sent anywhere. All numbers shown in <strong style={{ color: PALETTE.navy, fontWeight: 700 }}>{p.code}</strong>.
        </p>

        <div className="roi-grid" style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.2fr)',
          gap: 'clamp(24px, 4vw, 48px)',
          alignItems: 'start',
        }}>
          {/* Inputs */}
          <div>
            <Field
              label="Ops people drowning in spreadsheets"
              value={teamSize} onChange={setTeamSize}
              min={1} max={20} suffix="people" testid="roi-team-size"
            />
            <Field
              label="Hours / week each spends on manual ops admin"
              value={hoursPerWeek} onChange={setHoursPerWeek}
              min={1} max={30} suffix="h/wk" testid="roi-hours-per-week"
            />
            <Field
              label="Average loaded annual salary"
              value={annualSalary} onChange={setAnnualSalary}
              min={salaryMin} max={salaryMax} step={salaryStep}
              testid="roi-salary"
              formatValue={MONEY}
            />
          </div>

          {/* Outputs */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
            <Stat label="Hours reclaimed / year" value={NUMBER(result.hoursReclaimed) + ' hrs'} accent={PALETTE.navy} />
            <Stat label="Value reclaimed / year" value={MONEY(result.valueReclaimed)} accent={PALETTE.magenta} />
            <Stat label="Working weeks back" value={`${result.weeksReclaimed.toFixed(1)} wks`} />
            <Stat label="Per person, per year" value={MONEY(result.valueReclaimed / Math.max(1, teamSize))} accent={PALETTE.gold} />

            <div style={{
              gridColumn: '1 / -1',
              marginTop: 8,
              padding: '18px 22px',
              background: result.isMeaningful ? '#FFF8E7' : '#F4F4F4',
              borderLeft: `3px solid ${result.isMeaningful ? PALETTE.gold || '#C9941F' : PALETTE.muted}`,
              fontSize: 14, lineHeight: 1.55, color: PALETTE.ink,
            }} data-testid="roi-summary">
              {result.isMeaningful ? (
                <>
                  That is <strong style={{ color: PALETTE.navy }}>{MONEY(result.valueReclaimed)}</strong> of skilled time per year going into spreadsheets instead of product, and that is before retailer chargeback avoidance, faster audit response, and one less audit-week panic. Bring these numbers to the call and we will size a quote against them.
                </>
              ) : (
                <>
                  At this scale, time savings alone may not be the reason to move. Keystone still earns its place if (a) you are losing money on retailer chargebacks for short-dated stock, (b) a single BRCGS / IFS / national-food-authority audit failure would cost you a major retailer, or (c) your time-to-trace is blocking a new retailer onboarding. <a href="/site/contact" style={{ color: PALETTE.magenta, fontWeight: 600 }}>Talk to us</a>, happy to be honest about whether you are ready yet.
                </>
              )}
            </div>
          </div>
        </div>

        <p style={{
          fontSize: 12.5, color: PALETTE.muted, lineHeight: 1.55,
          margin: '36px 0 0', maxWidth: 760,
        }}>
          Loaded salary divided by 1,872 working hours/year (36hr × 52wk) to derive an hourly rate. 48 working weeks/year to allow for leave. Output is illustrative only, the real ROI conversation happens on a 20-minute call.
        </p>
      </div>
      <style>{`
        @media (max-width: 780px) {
          .roi-grid { grid-template-columns: 1fr !important; gap: 28px !important; }
        }
      `}</style>
    </section>
  );
}
